/**
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import cleanCords from '@turf/clean-coords';
import geojsonBbox from 'geojson-bounding-box';
import isEmpty from 'lodash.isempty';
import {getDefaultImageStyle} from 'ol/format/KML';
import ol from 'openlayers';
import simplepolygon from 'simplepolygon';
import svgpath from 'svgpath';
import {v4 as uuidv4} from 'uuid';

import ConfigUtils from '../utils/ConfigUtils';
import CoordinatesUtils from '../utils/CoordinatesUtils';
import {END_MARKERS, POINT_SHAPES, computeFeatureStyle} from '../utils/FeatureStyles';
import MiscUtils from './MiscUtils';


const VectorLayerUtils = {
    createPrintHighlighParams(layers, printCrs, mapScale, dpi = 96, scaleFactor = 1.0) {
        const qgisServerVersion = ConfigUtils.getConfigProp("qgisServerVersion", null, 3);
        const params = {
            geoms: [],
            styles: [],
            labels: [],
            labelFillColors: [],
            labelOutlineColors: [],
            labelOutlineSizes: [],
            labelRotations: [],
            labelSizes: [],
            labelDist: [],
            labelFrameColors: [],
            labelFrameOutlines: [],
            labelFrameOutlineWidths: [],
            labelFrameSizes: []
        };
        let ensureHex = null;
        let ensureHexRgb = null;
        if (qgisServerVersion >= 3) {
            ensureHex = (rgba) => (!Array.isArray(rgba) ? rgba : '#' + [255 - (rgba.length > 3 ? rgba[3] : 1) * 255, ...rgba.slice(0, 3)].map(v => Math.round(v).toString(16).padStart(2, '0')).join(''));
            ensureHexRgb = (rgb) => (!Array.isArray(rgb) ? rgb : '#' + [...rgb.slice(0, 3)].map(v => Math.round(v).toString(16).padStart(2, '0')).join(''));
        } else {
            ensureHexRgb = ensureHex = (rgb) => (!Array.isArray(rgb) ? rgb : ('#' + (0x1000000 + (rgb[2] | (rgb[1] << 8) | (rgb[0] << 16))).toString(16).slice(1)));
        }

        for (const layer of layers.slice(0).reverse()) {
            if (layer.type !== 'vector' || (layer.features || []).length === 0 || layer.visibility === false || layer.skipPrint === true) {
                continue;
            }
            const features = layer.features.map(feature => {
                if (feature.geometry?.coordinates) {
                    feature = {...feature, geometry: {...feature.geometry, coordinates: VectorLayerUtils.removeDuplicateNodes(feature.geometry.coordinates)}};
                }
                try {
                    return VectorLayerUtils.simplifyFeature(feature);
                } catch {
                    /* eslint-disable-next-line */
                    console.warn("Skipping invalid geometry");
                    return null;
                }
            }).filter(Boolean).flat();
            for (const feature of features) {
                if (!VectorLayerUtils.validateGeometry(feature.geometry)) {
                    continue;
                }
                const styleOptions = computeFeatureStyle(layer, feature);

                const properties = feature.properties || {};
                const geometry = VectorLayerUtils.reprojectGeometry(feature.geometry, feature.crs || printCrs, printCrs);
                if (feature.geometry.type === "LineString") {
                    // Generate arrow heads
                    if (styleOptions.headmarker) {
                        VectorLayerUtils.generateMarkerGeometry(params, styleOptions.headmarker, false, feature, layer, mapScale, dpi, scaleFactor);
                    }
                    if (styleOptions.tailmarker) {
                        VectorLayerUtils.generateMarkerGeometry(params, styleOptions.tailmarker, true, feature, layer, mapScale, dpi, scaleFactor);
                    }
                }
                if (feature.geometry.type === "LineString" && !isEmpty(properties.segment_labels)) {
                    // Split line into single segments and label them individually
                    const coords = geometry.coordinates;
                    for (let i = 0; i < coords.length - 1; ++i) {
                        const segment = {
                            type: "LineString",
                            coordinates: [coords[i], coords[i + 1]]
                        };
                        params.styles.push(VectorLayerUtils.createSld(segment.type, feature.styleName, styleOptions, layer.opacity, dpi, scaleFactor));
                        params.labels.push(properties.segment_labels[i] || " ");
                        params.geoms.push(VectorLayerUtils.geoJSONGeomToWkt(segment));
                        params.labelFillColors.push(ensureHex(styleOptions.textFill));
                        params.labelOutlineColors.push(ensureHex(styleOptions.textStroke));
                        params.labelOutlineSizes.push(scaleFactor);
                        params.labelSizes.push(Math.round(10 * scaleFactor));
                        params.labelDist.push("-5");
                        params.labelRotations.push("0");
                        params.labelFrameColors.push("white");
                        params.labelFrameOutlines.push("white");
                        params.labelFrameOutlineWidths.push("0");
                        params.labelFrameSizes.push("0");

                    }
                } else if (feature.styleName === "text" || feature.styleName === "textlabel") {
                    const x = geometry.coordinates[0];
                    const y = geometry.coordinates[1];
                    if (feature.styleName === "textlabel") {
                        if (qgisServerVersion < 4) {
                            // QGIS 3.x fallback for label frame
                            const metrics = MiscUtils.measureText(feature.properties.label, `10pt sans`);
                            const scale = styleOptions.strokeWidth * scaleFactor;
                            const mapWidth = CoordinatesUtils.pixelsToMapUnits((metrics.width + 12) * scale, 100, mapScale, printCrs);
                            const mapHeight = CoordinatesUtils.pixelsToMapUnits((metrics.height + 12) * scale, 100, mapScale, printCrs);
                            const frame = {
                                type: "Polygon",
                                coordinates: [[
                                    [x - 0.5 * mapWidth, y - 0.5 * mapHeight],
                                    [x + 0.5 * mapWidth, y - 0.5 * mapHeight],
                                    [x + 0.5 * mapWidth, y + 0.5 * mapHeight],
                                    [x - 0.5 * mapWidth, y + 0.5 * mapHeight],
                                    [x - 0.5 * mapWidth, y - 0.5 * mapHeight]
                                ]]
                            };
                            params.styles.push(VectorLayerUtils.createSld("Polygon", "default", {strokeColor: styleOptions.strokeColor, strokeWidth: styleOptions.strokeWidth, fillColor: styleOptions.fillColor}, layer.opacity, dpi, scaleFactor));
                            params.labels.push(" ");
                            params.geoms.push(VectorLayerUtils.geoJSONGeomToWkt(frame));
                            params.labelFillColors.push(ensureHex(styleOptions.textFill));
                            params.labelOutlineColors.push(ensureHex(styleOptions.textStroke));
                            params.labelOutlineSizes.push("0");
                            params.labelSizes.push("1");
                            params.labelDist.push("0");
                            params.labelRotations.push("0");
                        }

                        if (x !== styleOptions.anchor[0] && y !== styleOptions.anchor[1]) {
                            // Draw leader line
                            params.styles.push(VectorLayerUtils.createSld("LineString", "default", {strokeColor: styleOptions.strokeColor, strokeWidth: styleOptions.strokeWidth}, layer.opacity, dpi, scaleFactor));
                            params.labels.push(" ");
                            params.geoms.push(VectorLayerUtils.geoJSONGeomToWkt({type: "LineString", coordinates: [[x, y], styleOptions.anchor]}));
                            params.labelFillColors.push(ensureHex(styleOptions.textFill));
                            params.labelOutlineColors.push(ensureHex(styleOptions.textStroke));
                            params.labelOutlineSizes.push("0");
                            params.labelSizes.push("1");
                            params.labelDist.push("0");
                            params.labelRotations.push("0");
                            params.labelFrameColors.push("white");
                            params.labelFrameOutlines.push("white");
                            params.labelFrameOutlineWidths.push(0);
                            params.labelFrameSizes.push(0);
                        }
                    }
                    // Make point a tiny square, so that QGIS server centers the text inside the polygon when labelling
                    const square = {
                        type: "Polygon",
                        coordinates: [[
                            [x - 0.01, y - 0.01],
                            [x + 0.01, y - 0.01],
                            [x + 0.01, y + 0.01],
                            [x - 0.01, y + 0.01],
                            [x - 0.01, y - 0.01]
                        ]]
                    };
                    params.styles.push(VectorLayerUtils.createSld(square.type, feature.styleName, styleOptions, 0, dpi, scaleFactor));
                    params.labels.push(properties.label || " ");
                    params.geoms.push(VectorLayerUtils.geoJSONGeomToWkt(square));
                    if (feature.styleName === "textlabel") {
                        // QGIS 4.x label frame
                        params.labelFillColors.push(ensureHex(styleOptions.strokeColor));
                        params.labelOutlineColors.push(ensureHex(MiscUtils.isBrightColor(styleOptions.strokeColor) ? "#333" : "#FFF"));
                        params.labelFrameColors.push(ensureHexRgb(styleOptions.fillColor));
                        params.labelFrameOutlines.push(ensureHexRgb(styleOptions.strokeColor));
                        params.labelFrameOutlineWidths.push(styleOptions.strokeWidth * 0.25);
                        params.labelFrameSizes.push(qgisServerVersion >= 4 ? 2 : 0);
                    } else {
                        params.labelFillColors.push(ensureHex(styleOptions.textFill));
                        params.labelOutlineColors.push(ensureHex(styleOptions.textStroke));
                        params.labelFrameColors.push("white");
                        params.labelFrameOutlines.push("white");
                        params.labelFrameOutlineWidths.push(0);
                        params.labelFrameSizes.push(0);
                    }
                    params.labelOutlineSizes.push(scaleFactor * styleOptions.strokeWidth * 0.5);
                    params.labelSizes.push(Math.round(10 * styleOptions.strokeWidth * scaleFactor));
                    params.labelDist.push("5");
                    params.labelRotations.push(((properties.rotation || 0) / Math.PI * 180).toFixed(0));
                } else {
                    params.styles.push(VectorLayerUtils.createSld(geometry.type, feature.styleName, styleOptions, layer.opacity, dpi, scaleFactor));
                    params.labels.push(properties.label || " ");
                    params.geoms.push(VectorLayerUtils.geoJSONGeomToWkt(geometry));
                    params.labelFillColors.push(ensureHex(styleOptions.textFill));
                    params.labelOutlineColors.push(ensureHex(styleOptions.textStroke));
                    params.labelOutlineSizes.push(scaleFactor);
                    params.labelSizes.push(Math.round(10 * scaleFactor));
                    params.labelDist.push("-5");
                    params.labelRotations.push("0");
                    params.labelFrameColors.push("white");
                    params.labelFrameOutlines.push("white");
                    params.labelFrameOutlineWidths.push(0);
                    params.labelFrameSizes.push(0);
                }
                if (properties.end_label) {
                    const coo = geometry.coordinates;
                    let point = null;
                    if (feature.geometry.type === "LineString") {
                        point = coo[coo.length - 1];
                    } else if (feature.geometry.type === "Polygon") {
                        point = coo[0].reduce((min, p) => p[1] < min[1] ? p : min, [Infinity, Infinity]);
                    }
                    if (point) {
                        // Add extra endpoint with label
                        params.styles.push(VectorLayerUtils.createSld('Point', 'text', {}, 255, dpi, scaleFactor));
                        params.labels.push(properties.end_label);
                        params.geoms.push(VectorLayerUtils.geoJSONGeomToWkt({type: "Point", coordinates: point}));
                        params.labelFillColors.push(ensureHex(styleOptions.textFill));
                        params.labelOutlineColors.push(ensureHex(styleOptions.textStroke));
                        params.labelOutlineSizes.push(scaleFactor);
                        params.labelSizes.push(Math.round(10 * scaleFactor));
                        params.labelDist.push("-5");
                        params.labelRotations.push("0");
                        params.labelFrameColors.push("white");
                        params.labelFrameOutlines.push("white");
                        params.labelFrameOutlineWidths.push(0);
                        params.labelFrameSizes.push(0);
                    }
                }
            }
        }
        return params;
    },
    removeDuplicateNodes(coordinates) {
        if (Array.isArray(coordinates[0][0])) {
            return coordinates.map(VectorLayerUtils.removeDuplicateNodes);
        } else if (Array.isArray(coordinates[0])) {
            return coordinates.filter((item, pos, arr) => {
                return pos === 0 || item[0] !== arr[pos - 1][0] || item[1] !== arr[pos - 1][1];
            });
        } else {
            return coordinates;
        }
    },
    simplifyFeature(feature) {
        if (!feature.geometry) {
            return feature;
        } else if (feature.geometry.type === "MultiPolygon") {
            return feature.geometry.coordinates.map(part => {
                return VectorLayerUtils.simplifyFeature({
                    ...feature,
                    geometry: {type: "Polygon", coordinates: part}
                });
            }).flat();
        } else if (feature.geometry.type === "Polygon") {
            return simplepolygon(cleanCords(feature)).features.map((feat, idx, features) => {
                if (feat.properties.parent >= 0) {
                    features[feat.properties.parent].geometry.coordinates.push(feat.geometry.coordinates[0]);
                    return null;
                }
                return feat;
            }).filter(x => x).map(feat => ({...feature, geometry: feat.geometry}));
        } else {
            return feature;
        }
    },
    validateGeometry(geometry) {
        if (!geometry) {
            return false;
        }
        if (geometry.type === "GeometryCollection") {
            return geometry.geometries.every(VectorLayerUtils.validateGeometry);
        }
        if (geometry.type === "Point") {
            return !isEmpty(geometry.coordinates);
        }
        const minLength = geometry.type.endsWith("LineString") ? 2 : 3;
        const isDegenerate = (coordinates) => {
            if (Array.isArray(coordinates[0][0])) {
                return coordinates.map(isDegenerate).find(entry => entry === false);
            } else {
                return coordinates.length < minLength;
            }
        };
        return !isDegenerate(geometry.coordinates);
    },
    createSld(geometrytype, styleName, styleOptions, layerOpacity, dpi = 96, scaleFactor = 1.0) {
        let opts = {};
        const dpiScale = dpi / 96 * scaleFactor;
        // Special cases
        if (styleName === 'text') {
            // Make geometry transparent
            opts = {
                strokeColor: [0, 0, 0, 0],
                fillColor: [0, 0, 0, 0],
                strokeWidth: 0
            };
        } else if (styleName === 'marker') {
            opts = {
                strokeColor: [0, 0, 255, 1],
                strokeWidth: 1 * dpiScale,
                fillColor: [255, 255, 255, 1],
                circleRadius: 2 * dpiScale
            };
        } else {
            // Default style
            opts = styleOptions;
        }

        const ensureHex = (rgb) => (!Array.isArray(rgb) ? rgb : ('#' + (0x1000000 + (rgb[2] | (rgb[1] << 8) | (rgb[0] << 16))).toString(16).slice(1)));
        const opacity =  (rgb) => {
            if (Array.isArray(rgb) && rgb.length > 3) {
                return rgb[3] * layerOpacity / 255;
            }
            return 1 * layerOpacity / 255;
        };

        const stroke = '<se:Stroke>' +
                     '<se:SvgParameter name="stroke">' + ensureHex(opts.strokeColor) + '</se:SvgParameter>' +
                     '<se:SvgParameter name="stroke-opacity">' + opacity(opts.strokeColor) + '</se:SvgParameter>' +
                     '<se:SvgParameter name="stroke-width">' + (opts.strokeWidth * dpiScale) + '</se:SvgParameter>' +
                     '<se:SvgParameter name="stroke-linejoin">round</se:SvgParameter>' +
                     '<se:SvgParameter name="stroke-linecap">butt</se:SvgParameter>' +
                     (!isEmpty(opts.strokeDash) ? '<se:SvgParameter name="stroke-dasharray">' + opts.strokeDash.map(x => x * dpiScale).join(' ') + '</se:SvgParameter>' : '') +
                     '</se:Stroke>';
        const fill = '<se:Fill>' +
                   '<se:SvgParameter name="fill">' + ensureHex(opts.fillColor) + '</se:SvgParameter>' +
                   '<se:SvgParameter name="fill-opacity">' + opacity(opts.fillColor) + '</se:SvgParameter>' +
                   '</se:Fill>';

        let rule = null;
        if (geometrytype.endsWith("Point")) {
            // POINT_SHAPES keys are valid SLD WellKnownNames, but validate before interpolating into XML
            const wellKnownName = Object.hasOwn(POINT_SHAPES, opts.pointShape) ? opts.pointShape : 'circle';
            rule = '<se:PointSymbolizer>' +
                   '<se:Graphic>' +
                   '<se:Mark>' +
                   '<se:WellKnownName>' + wellKnownName + '</se:WellKnownName>' +
                   '<se:Stroke>' +
                   '<se:SvgParameter name="stroke">' + ensureHex(opts.strokeColor) + '</se:SvgParameter>' +
                   '<se:SvgParameter name="stroke-opacity">' + opacity(opts.strokeColor) + '</se:SvgParameter>' +
                   '<se:SvgParameter name="stroke-width">' + (opts.strokeWidth * dpiScale) + '</se:SvgParameter>' +
                   '<se:SvgParameter name="stroke-linejoin">round</se:SvgParameter>' +
                   '</se:Stroke>' +
                   fill +
                   '</se:Mark>' +
                   '<se:Size>' + (2 * opts.circleRadius * dpiScale) + '</se:Size>' +
                   '</se:Graphic>' +
                   '</se:PointSymbolizer>';
        } else if (geometrytype.endsWith("LineString")) {
            rule = '<se:LineSymbolizer>' +
                   stroke +
                   '</se:LineSymbolizer>';
        } else if (geometrytype.endsWith("Polygon")) {
            rule = '<se:PolygonSymbolizer>' +
                   stroke +
                   fill +
                   '</se:PolygonSymbolizer>';
        }
        if (rule) {
            return '<?xml version="1.0" encoding="UTF-8"?>' +
                   '<StyledLayerDescriptor xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" version="1.1.0" xmlns:xlink="http://www.w3.org/1999/xlink" xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.1.0/StyledLayerDescriptor.xsd" xmlns:se="http://www.opengis.net/se">' +
                   '<UserStyle>' +
                   '<se:FeatureTypeStyle>' +
                   '<se:Rule>' +
                   rule +
                   '</se:Rule>' +
                   '</se:FeatureTypeStyle>' +
                   '</UserStyle>' +
                   '</StyledLayerDescriptor>';
        }
        return null;
    },
    generateMarkerGeometry(params, markername, tail, feature, layer, mapScale, dpi, scaleFactor) {
        if (!END_MARKERS[markername]) {
            return;
        }
        const marker = END_MARKERS[markername];
        // Read the SVG and generate a matching WKT geometry for the marker
        let path = '';
        let width = 0;
        let height = 0;
        try {
            const parser = new DOMParser();
            const svgSrc = atob(marker.src.slice(26));
            const svgDoc = parser.parseFromString(svgSrc, "text/xml");
            width = parseInt(svgDoc.getElementsByTagName("svg")[0].getAttribute("width"), 10);
            height = parseInt(svgDoc.getElementsByTagName("svg")[0].getAttribute("height"), 10);
            path = svgDoc.getElementsByTagName("path")[0].getAttribute("d");
        } catch {
            /* eslint-disable-next-line */
            console.warn("Could not parse path for marker " + markername);
            return;
        }
        const opts = feature.styleOptions;
        //                        [              Same as in FeatureStyles.js             ]   [   pixel to map units  ]
        const markerScaleFactor = 0.125 * (1 + opts.strokeWidth) * (opts.markerscale ?? 1) / dpi * 0.0254 * mapScale * scaleFactor;
        const origin = feature.geometry.coordinates[tail ? feature.geometry.coordinates.length - 1 : 0];
        const p2 = feature.geometry.coordinates[tail ? feature.geometry.coordinates.length - 2 : 1];
        const coordinates = [];
        const angle = 0.5 * Math.PI + Math.atan2(origin[0] - p2[0], origin[1] - p2[1]);
        const alpha = marker.baserotation / 180 * Math.PI + angle;
        const cosa = Math.cos(alpha);
        const sina = Math.sin(alpha);
        svgpath(path).iterate((segment, index, x, y) => {
            // Skip move instructions
            if (["m", "M"].includes(segment[0])) {
                return;
            }
            const dx = (x - marker.anchor[0] * width) * markerScaleFactor;
            const dy = (y - marker.anchor[1] * height) * markerScaleFactor;
            const rx = cosa * dx + sina * dy;
            const ry = -sina * dx + cosa * dy;
            coordinates.push([origin[0] + rx, origin[1] + ry]);
        });
        // Closing coordinate
        coordinates.push(coordinates[0]);
        const geometry = {
            type: "Polygon",
            coordinates: [coordinates]
        };
        const styleOptions = {
            strokeWidth: 1,
            strokeDash: [],
            strokeColor: feature.styleOptions.strokeColor,
            fillColor: feature.styleOptions.strokeColor
        };

        params.styles.push(VectorLayerUtils.createSld(geometry.type, "default", styleOptions, layer.opacity, dpi, scaleFactor));
        params.geoms.push(VectorLayerUtils.geoJSONGeomToWkt(geometry));
        params.labels.push(" ");
        params.labelFillColors.push("#FFF");
        params.labelOutlineColors.push("#FFF");
        params.labelOutlineSizes.push(scaleFactor);
        params.labelSizes.push(Math.round(10 * scaleFactor));
        params.labelDist.push("0");
    },
    reprojectGeometry(geometry, srccrs, dstcrs) {
        if (srccrs === dstcrs || !srccrs || !dstcrs) {
            return geometry;
        }
        if (geometry.type === "Point") {
            const wgscoo = CoordinatesUtils.reproject(geometry.coordinates, srccrs, dstcrs);
            return {
                type: geometry.type,
                coordinates: wgscoo
            };
        } else if (geometry.type === "LineString" || geometry.type === "MultiPoint") {
            return {
                type: geometry.type,
                coordinates: geometry.coordinates.map(tuple => {
                    return CoordinatesUtils.reproject(tuple, srccrs, dstcrs);
                })
            };
        } else if (geometry.type === "Polygon" || geometry.type === "MultiLineString") {
            return {
                type: geometry.type,
                coordinates: geometry.coordinates.map(ring => {
                    return ring.map(tuple => {
                        return CoordinatesUtils.reproject(tuple, srccrs, dstcrs);
                    });
                })
            };
        } else if (geometry.type === "MultiPolygon") {
            return {
                type: geometry.type,
                coordinates: geometry.coordinates.map(part => {
                    return part.map(ring => {
                        return ring.map(tuple => {
                            return CoordinatesUtils.reproject(tuple, srccrs, dstcrs);
                        });
                    });
                })
            };
        } else {
            return geometry;
        }
    },
    reprojectFeature(feature, srccrs, dstcrs) {
        if (srccrs === dstcrs || !srccrs || !dstcrs) {
            return feature;
        }
        if (feature.features) {
            return {
                ...feature,
                features: feature.features.map(f => VectorLayerUtils.reprojectFeature(srccrs, dstcrs))
            };
        } else {
            return {
                ...feature,
                geometry: VectorLayerUtils.reprojectGeometry(feature.geometry, srccrs, dstcrs)
            };
        }
    },
    wktToGeoJSON(wkt, srccrs, dstcrs, id = uuidv4()) {
        wkt = wkt
            .replace(/Point(\w+)/i, "Point $1")
            .replace(/LineString(\w+)/i, "LineString $1")
            .replace(/Polygon(\w+)/i, "Polygon $1")
            .replace(/MultiSurface(\w*)/i, "GeometryCollection $1");
        try {
            const feature = new ol.format.WKT().readFeature(wkt, {
                dataProjection: srccrs,
                featureProjection: dstcrs
            });
            const featureObj = new ol.format.GeoJSON().writeFeatureObject(feature);
            featureObj.id = id;
            return featureObj;
        } catch {
            /* eslint-disable-next-line */
            console.warn("Failed to parse geometry: " + wkt);
            return null;
        }
    },
    geoJSONGeomToWkt(gj, precision = undefined) {
        if (precision === undefined) {
            precision = ConfigUtils.getConfigProp("wmsWktPrecision", null, 4);
        }
        if (gj.type === 'Feature') {
            gj = gj.geometry;
        }

        const wrapParens = (s) =>  { return '(' + s + ')'; };
        const pairWKT = (c) => { return c.map(x => x.toFixed(precision)).join(' '); };
        const ringWKT = (r) => { return r.map(pairWKT).join(', '); };
        const ringsWKT = (r) => { return r.map(ringWKT).map(wrapParens).join(', '); };
        const multiRingsWKT = (r) => { return r.map(ringsWKT).map(wrapParens).join(', '); };

        switch (gj.type) {
        case 'Point':
            return 'POINT (' + pairWKT(gj.coordinates) + ')';
        case 'LineString':
            return 'LINESTRING (' + ringWKT(gj.coordinates) + ')';
        case 'Polygon':
            return 'POLYGON (' + ringsWKT(gj.coordinates) + ')';
        case 'MultiPoint':
            return 'MULTIPOINT (' + ringWKT(gj.coordinates) + ')';
        case 'MultiPolygon':
            return 'MULTIPOLYGON (' + multiRingsWKT(gj.coordinates) + ')';
        case 'MultiLineString':
            return 'MULTILINESTRING (' + ringsWKT(gj.coordinates) + ')';
        case 'GeometryCollection':
            return 'GEOMETRYCOLLECTION (' + gj.geometries.map(
                (x) => VectorLayerUtils.geoJSONGeomToWkt(x, precision)
            ).join(', ') + ')';
        default:
            throw new Error('Invalid geometry object');
        }
    },
    kmlToGeoJSON(kml) {
        const kmlFormat = new ol.format.KML({defaultStyle: [new ol.style.Style()]});
        const geojsonFormat = new ol.format.GeoJSON();
        const features = [];
        for (const olFeature of kmlFormat.readFeatures(kml)) {
            let style = olFeature.getStyleFunction()(olFeature);
            style = style[0] || style;

            const styleOptions = {
                strokeColor: style.getStroke() ? style.getStroke().getColor() : [0, 0, 0, 1],
                strokeWidth: style.getStroke() ? style.getStroke().getWidth() : 1,
                strokeDash: style.getStroke() ? style.getStroke().getLineDash() : [],
                fillColor: style.getFill() ? style.getFill().getColor() : [255, 255, 255, 1],
                textFill: style.getText() && style.getText().getFill() ? style.getText().getFill().getColor() : [0, 0, 0, 1],
                textStroke: style.getText() && style.getText().getStroke() ? style.getText().getStroke().getColor() : [255, 255, 255, 1]
            };
            if (style.getImage() && style.getImage() !== getDefaultImageStyle() && style.getImage().getSrc()) {
                // FIXME: Uses private members of ol.style.Icon, style.getImage().getAnchor() returns null because style.getImage.getSize() is null because the the image is not yet loaded
                const anchor = style.getImage().anchor_ || [0.5, 0.5];
                const anchorOrigin = (style.getImage().anchorOrigin_ || "").split("-");
                if (anchorOrigin.includes("right")) {
                    anchor[0] = 1 - anchor[0];
                }
                if (anchorOrigin.includes("bottom")) {
                    anchor[1] = 1 - anchor[1];
                }
                styleOptions.iconSrc = style.getImage().getSrc();
                styleOptions.iconAnchor = anchor;
            }
            const feature = geojsonFormat.writeFeatureObject(olFeature);
            Object.assign(feature, {
                styleName: styleOptions.iconSrc && feature.geometry?.type === "Point" ? 'marker' : 'default',
                styleOptions: styleOptions,
                id: uuidv4(),
                crs: "EPSG:4326",
                properties: {}
            });
            const properties = olFeature.getProperties();
            const excludedProperties = ['visibility', olFeature.getGeometryName()];
            for (const key of Object.keys(properties)) {
                if (!excludedProperties.includes(key)) {
                    feature.properties[key] = properties[key];
                }
            }
            if (properties.name && feature.styleName === 'marker') {
                feature.properties.label = properties.name;
            }
            features.push(feature);
        }
        return features;
    },
    convert3dto2d(entry) {
        if (!Array.isArray(entry)) {
            return entry;
        } else if (entry.length >= 3 && !Array.isArray(entry[0])) {
            return [entry[0], entry[1]];
        } else if (Array.isArray(entry[0])) {
            return entry.map(VectorLayerUtils.convert3dto2d);
        }
        return entry;
    },
    computeFeaturesBBox(features) {
        const featureCrs = new Set();
        features.forEach(feature => {
            if (feature.crs) {
                featureCrs.add(feature.crs);
            }
        });
        const bboxCrs = featureCrs.size === 1 ? [...featureCrs.keys()][0] : "EPSG:4326";
        const collectionFeatures = features.filter(feature => feature.geometry).map(feature => ({
            ...feature,
            geometry: feature.crs ? VectorLayerUtils.reprojectGeometry(feature.geometry, feature.crs, bboxCrs) : feature.geometry
        }));
        if (isEmpty(collectionFeatures)) {
            return null;
        }
        let bounds = geojsonBbox({
            type: "FeatureCollection",
            features: collectionFeatures
        });
        // Discard z component
        if (bounds.length === 6) {
            bounds = [bounds[0], bounds[1], bounds[3], bounds[4]];
        }
        return {
            crs: bboxCrs,
            bounds: bounds
        };
    },
    computeFeatureBBox(feature) {
        let bounds = geojsonBbox(feature);
        // Discard z component
        if (bounds.length === 6) {
            bounds = [bounds[0], bounds[1], bounds[3], bounds[4]];
        }
        return bounds;
    },
    getFeatureCenter(feature) {
        const geojson = new ol.format.GeoJSON().readFeature(feature);
        const geometry = geojson.getGeometry();
        const type = geometry.getType();
        let center = null;
        switch (type) {
        case "Polygon":
            center = geometry.getInteriorPoint().getCoordinates();
            break;
        case "MultiPolygon":
            center = geometry.getInteriorPoints().getClosestPoint(ol.extent.getCenter(geometry.getExtent()));
            break;
        case "Point":
            center = geometry.getCoordinates();
            break;
        case "MultiPoint":
            center = geometry.getClosestPoint(ol.extent.getCenter(geometry.getExtent()));
            break;
        case "LineString":
            center = geometry.getCoordinateAt(0.5);
            break;
        case "MultiLineString":
            center = geometry.getClosestPoint(ol.extent.getCenter(geometry.getExtent()));
            break;
        case "Circle":
            center = geometry.getCenter();
            break;
        default:
            break;
        }
        return center;
    }
};

export default VectorLayerUtils;
